import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isRequestCancellation } from "@shared/api/client";
import {
  getAdminPanelData,
  type AdminPanelSectionPayload,
} from "../api/admin-panel";
import type {
  AdminClockingData,
  AdminSection,
  ContractRecord,
  AdminPanelSummary,
  AdminPanelUser,
  AdminStatisticsData,
  PayrollRecord,
  VacationRequest,
} from "../types/admin-panel";

const emptySummary: AdminPanelSummary = {
  users_count: 0,
  vacation_requests_count: 0,
  payrolls_count: 0,
  contracts_count: 0,
};

type ClockingRange = {
  from: string;
  to: string;
};

function todayIso(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isAbortError(error: unknown) {
  return isRequestCancellation(error);
}

function isTimeoutError(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "ECONNABORTED"
  );
}

function errorMessage(error: unknown) {
  if (isTimeoutError(error)) {
    return "La carga del panel está tardando más de lo habitual. Pulsa Actualizar si no se completa.";
  }

  return "No se pudieron cargar los datos del panel de administración.";
}

function sectionsForAdminPanelSection(
  activeSection: AdminSection,
): AdminPanelSectionPayload[] {
  if (activeSection === "usuarios") return ["summary", "users"];
  if (activeSection === "horarios" || activeSection === "agenda") return ["users"];
  if (activeSection === "fichajes") return ["clocking"];
  if (activeSection === "nominas") return ["users", "payrolls"];
  if (activeSection === "contratos") return ["users", "contracts"];
  if (activeSection === "vacaciones") return ["vacations"];
  return ["statistics"];
}

function uniqueSections(
  sections: AdminPanelSectionPayload[],
): AdminPanelSectionPayload[] {
  return Array.from(new Set(sections));
}

export function useAdminPanel(activeSection: AdminSection) {
  const [summary, setSummary] = useState<AdminPanelSummary>(emptySummary);
  const [users, setUsers] = useState<AdminPanelUser[]>([]);
  const [vacations, setVacations] = useState<VacationRequest[]>([]);
  const [payrolls, setPayrolls] = useState<PayrollRecord[]>([]);
  const [contracts, setContracts] = useState<ContractRecord[]>([]);
  const [vacationRejectionReasons, setVacationRejectionReasons] = useState<
    string[]
  >([]);
  const [clocking, setClocking] = useState<AdminClockingData | null>(null);
  const [statistics, setStatistics] = useState<AdminStatisticsData | null>(null);
  const [statisticsYear, setStatisticsYearState] = useState(() => new Date().getFullYear());
  const [clockingRange, setClockingRangeState] = useState<ClockingRange>(() => {
    const today = todayIso();
    return { from: today, to: today };
  });
  const [clockingRankPeriod, setClockingRankPeriodState] = useState<
    "week" | "month" | "year"
  >("month");
  const [rankingLoading, setRankingLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingSections, setLoadingSections] = useState<
    Set<AdminPanelSectionPayload>
  >(new Set());

  const mountedRef = useRef(true);
  const loadedSectionsRef = useRef<Set<AdminPanelSectionPayload>>(new Set());
  const inFlightSectionsRef = useRef<
    Map<AdminPanelSectionPayload, Promise<void>>
  >(new Map());
  const clockingRangeRef = useRef(clockingRange);
  const clockingRankPeriodRef = useRef(clockingRankPeriod);
  const statisticsYearRef = useRef(statisticsYear);

  const activeSections = useMemo(
    () => sectionsForAdminPanelSection(activeSection),
    [activeSection],
  );

  const loading = activeSections.some((section) =>
    loadingSections.has(section),
  );

  const updateLoadingSections = useCallback(
    (sections: AdminPanelSectionPayload[], enabled: boolean) => {
      if (!mountedRef.current || sections.length === 0) return;

      setLoadingSections((current) => {
        const next = new Set(current);
        sections.forEach((section) => {
          if (enabled) next.add(section);
          else next.delete(section);
        });
        return next;
      });
    },
    [],
  );

  const markSectionsLoaded = useCallback(
    (sections: AdminPanelSectionPayload[]) => {
      if (!mountedRef.current || sections.length === 0) return;
      sections.forEach((section) => loadedSectionsRef.current.add(section));
    },
    [],
  );

  const applyData = useCallback(
    (data: Awaited<ReturnType<typeof getAdminPanelData>>) => {
      if (!mountedRef.current) return;

      if (data.summary) setSummary(data.summary);
      if (data.users) setUsers(data.users);
      if (data.vacations) setVacations(data.vacations);
      if (data.payrolls) setPayrolls(data.payrolls);
      if (data.contracts) setContracts(data.contracts);
      if (data.vacation_rejection_reasons) {
        setVacationRejectionReasons(data.vacation_rejection_reasons);
      }
      if (data.clocking) setClocking(data.clocking);
      if (data.statistics) setStatistics(data.statistics);

      setLoadError(null);
    },
    [],
  );

  const loadSections = useCallback(
    async (
      sections: AdminPanelSectionPayload[],
      options?: {
        force?: boolean;
        clockingRange?: ClockingRange;
        clockingRankPeriod?: "week" | "month" | "year";
      },
    ) => {
      const requestedSections = uniqueSections(sections);
      if (requestedSections.length === 0) return;

      const force = options?.force === true;
      const alreadyInFlight = requestedSections
        .map((section) => inFlightSectionsRef.current.get(section))
        .filter((promise): promise is Promise<void> => Boolean(promise));

      const sectionsToRequest = requestedSections.filter((section) => {
        if (!force && loadedSectionsRef.current.has(section)) return false;
        return !inFlightSectionsRef.current.has(section);
      });

      if (sectionsToRequest.length === 0) {
        await Promise.allSettled(alreadyInFlight);
        return;
      }

      updateLoadingSections(sectionsToRequest, true);
      const range = options?.clockingRange ?? clockingRangeRef.current;

      const requestPromise = getAdminPanelData({
        sections: sectionsToRequest,
        clocking_date_from: range.from,
        clocking_date_to: range.to,
        clocking_rank_period:
          options?.clockingRankPeriod ?? clockingRankPeriodRef.current,
        statistics_year: statisticsYearRef.current,
      })
        .then((data) => {
          if (!mountedRef.current) return;
          applyData(data);
          markSectionsLoaded(sectionsToRequest);
        })
        .catch((error) => {
          if (!isAbortError(error)) {
            if (mountedRef.current) setLoadError(errorMessage(error));
            console.error("Error cargando Admin Panel", error);
          }
        })
        .finally(() => {
          sectionsToRequest.forEach((section) => {
            inFlightSectionsRef.current.delete(section);
          });
          updateLoadingSections(sectionsToRequest, false);
        });

      sectionsToRequest.forEach((section) => {
        inFlightSectionsRef.current.set(section, requestPromise);
      });

      await Promise.allSettled([...alreadyInFlight, requestPromise]);
    },
    [applyData, markSectionsLoaded, updateLoadingSections],
  );

  const reload = useCallback(
    async (options?: {
      clockingRange?: ClockingRange;
      clockingRankPeriod?: "week" | "month" | "year";
    }) => {
      await loadSections(activeSections, {
        force: true,
        clockingRange: options?.clockingRange,
        clockingRankPeriod: options?.clockingRankPeriod,
      });
    },
    [activeSections, loadSections],
  );

  const changeClockingRange = useCallback(
    async (from: string, to: string) => {
      const range = { from, to };
      setClockingRangeState(range);
      clockingRangeRef.current = range;

      await loadSections(["clocking"], {
        force: true,
        clockingRange: range,
        clockingRankPeriod: clockingRankPeriodRef.current,
      });
    },
    [loadSections],
  );

  const changeClockingRankPeriod = useCallback(
    async (period: "week" | "month" | "year") => {
      setClockingRankPeriodState(period);
      clockingRankPeriodRef.current = period;
      setRankingLoading(true);

      try {
        await loadSections(["clocking"], {
          force: true,
          clockingRange: clockingRangeRef.current,
          clockingRankPeriod: period,
        });
      } finally {
        if (mountedRef.current) setRankingLoading(false);
      }
    },
    [loadSections],
  );


  const changeStatisticsYear = useCallback(
    async (year: number) => {
      setStatisticsYearState(year);
      statisticsYearRef.current = year;

      await loadSections(["statistics"], {
        force: true,
      });
    },
    [loadSections],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void loadSections(activeSections);
  }, [activeSections, loadSections]);

  return {
    summary,
    users,
    vacations,
    payrolls,
    contracts,
    vacationRejectionReasons,
    loading,
    clocking,
    clockingRange,
    clockingRankPeriod,
    setClockingRankPeriod: changeClockingRankPeriod,
    setClockingRange: changeClockingRange,
    rankingLoading,
    statistics,
    statisticsYear,
    setStatisticsYear: changeStatisticsYear,
    reload,
    loadError,
  };
}
